import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography } from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import type { RiskAssessmentAiAssessmentDTO } from "../../types/riskAssessmentAiDtos";

const useStyles = makeStyles((theme) => ({
    description: { color: theme.palette.text.secondary, marginBottom: theme.spacing(2) },
    hazard: { marginBottom: theme.spacing(2) },
    hazardName: { marginBottom: theme.spacing(0.5) },
    listBlock: { marginTop: theme.spacing(1) },
    chips: { display: "flex", flexWrap: "wrap", gap: theme.spacing(0.5), marginTop: theme.spacing(0.5) },
    divider: { marginBottom: theme.spacing(2) },
    note: { color: theme.palette.text.secondary, display: "block", margin: theme.spacing(2, 0) },
}));

const lists = [
    { key: "controlsInPlace", label: "Controls in place" },
    { key: "additionalControls", label: "Additional controls required" },
] as const;

interface AiAssessmentQuickViewProps {
    assessment: RiskAssessmentAiAssessmentDTO | null;
    open: boolean;
    onClose: () => void;
}

// Read-only: a glance before committing to the review stage, where everything is editable.
export default function AiAssessmentQuickView({ assessment, open, onClose }: AiAssessmentQuickViewProps) {
    const classes = useStyles();

    return (
        <Dialog open={open && !!assessment} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
            <DialogTitle>{assessment?.title || "Draft assessment"}</DialogTitle>
            <DialogContent dividers>
                {assessment?.description && (
                    <Typography variant="body2" className={classes.description}>
                        {assessment.description}
                    </Typography>
                )}

                <div className={classes.listBlock}>
                    <Typography variant="caption" color="textSecondary">
                        People at risk
                    </Typography>
                    <div className={classes.chips}>
                        {assessment?.peopleAtRisk?.length ? (
                            assessment.peopleAtRisk.map((person, i) => <Chip key={i} size="small" label={person} />)
                        ) : (
                            <Typography variant="caption" color="textSecondary">
                                None
                            </Typography>
                        )}
                    </div>
                </div>

                <Typography variant="caption" className={classes.note}>
                    Severity and likelihood are set when you complete the assessment, not by the assistant.
                </Typography>

                {assessment?.hazards.map((hazard, index) => (
                    <div key={index} className={classes.hazard}>
                        {index > 0 && <Divider className={classes.divider} />}
                        <Typography variant="subtitle2" className={classes.hazardName}>
                            {index + 1}. {hazard.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {hazard.description}
                        </Typography>

                        {lists.map(({ key, label }) => (
                            <div key={key} className={classes.listBlock}>
                                <Typography variant="caption" color="textSecondary">
                                    {label}
                                </Typography>
                                <div className={classes.chips}>
                                    {hazard[key].length > 0 ? (
                                        hazard[key].map((item, i) => <Chip key={i} size="small" label={item} />)
                                    ) : (
                                        <Typography variant="caption" color="textSecondary">
                                            None
                                        </Typography>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" variant="outlined">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
